// Region crop: map a percentage selection (relative to the displayed image)
// to original pixels, then use canvas to cut out base64 (no data-URL prefix,
// matching what /product/image/upload expects). Port of
// apps/web/lib/crop-image.ts.

export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageDims {
  displayWidth: number;
  displayHeight: number;
  naturalWidth: number;
  naturalHeight: number;
}

/**
 * Pure: display-coordinate selection → original-pixel selection. The crop the
 * overlay produces is relative to the <img> render size; the upload wants the
 * original resolution, so multiply by natural/display ratio and floor.
 */
export function scaleCropToNatural(crop: PixelRect, dims: ImageDims): PixelRect {
  const sx = dims.naturalWidth / dims.displayWidth;
  const sy = dims.naturalHeight / dims.displayHeight;
  return {
    x: Math.floor(crop.x * sx),
    y: Math.floor(crop.y * sy),
    width: Math.floor(crop.width * sx),
    height: Math.floor(crop.height * sy),
  };
}

/**
 * Cut out the selection with canvas, return pure base64 (no
 * "data:image/...;base64," prefix). `image` must be loaded (naturalWidth > 0).
 * `crop` is in display coordinates; converted to original pixels internally.
 * Outputs JPEG (quality 0.85) for size.
 *
 * `maxDimension`: if the cropped long edge exceeds this, scale down so the long
 * edge = maxDimension before output, reducing mobile upload size. Default 1280.
 * Pass 0 / Infinity to disable the cap.
 */
export function cropImageToBase64(
  image: HTMLImageElement,
  crop: PixelRect,
  mime: "image/jpeg" | "image/png" = "image/jpeg",
  quality = 0.85,
  maxDimension = 1280,
): string {
  const nat = scaleCropToNatural(crop, {
    displayWidth: image.width,
    displayHeight: image.height,
    naturalWidth: image.naturalWidth,
    naturalHeight: image.naturalHeight,
  });

  const maxEdge = Math.max(nat.width, nat.height);
  const scale =
    maxDimension > 0 && Number.isFinite(maxDimension) && maxEdge > maxDimension
      ? maxDimension / maxEdge
      : 1;
  const outW = Math.max(1, Math.round(nat.width * scale));
  const outH = Math.max(1, Math.round(nat.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, nat.x, nat.y, nat.width, nat.height, 0, 0, outW, outH);
  const dataUrl = canvas.toDataURL(mime, quality);
  const idx = dataUrl.indexOf(",");
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}
